import {
    HubConnection,
    HubConnectionBuilder,
    LogLevel,
} from '@microsoft/signalr';

import { getPushNotificationsBaseUrl } from '../../components/portal-notification/helpers/notificationApi';
import store from '../../store/store';
import { getCircularReplacer } from '../helper';
import Logger from '../logger/logger';
import { getAccessToken } from '../session/sessionStore';

class SignalRHelper {
    public connection: HubConnection;
    public isConnected = false;

    private static readonly notificationHubUrl = '/hub/notification';

    constructor() {
        this.connection = new HubConnectionBuilder()
            .withUrl(`${getPushNotificationsBaseUrl()}${SignalRHelper.notificationHubUrl}`, { accessTokenFactory: () => getAccessToken() })
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect()
            .build();
    }

    public updateConnection() {
        if (this.isConnected) {
            return;
        }
        this.start();

        this.connection.onreconnecting(error => {
            this.isConnected = false;
            Logger.error([ 'portal-notification', 'signalR' ], `Connection lost due to error "${error}". Reconnecting.`);
        });

        this.connection.onreconnected(connectionId => {
            this.isConnected = true;
            Logger.debug([ 'portal-notification', 'signalR' ], `Connection reestablished. Connected with connectionId "${connectionId}".`);
        });

        this.connection.onclose(error => {
            this.isConnected = false;
            const errorMessage = `Connection closed due to error "${JSON.stringify(error, getCircularReplacer)}". Try refreshing this page to restart the connection.`;
            Logger.critical([ 'portal-notification', 'signalR' ], errorMessage, { state: store.state });
        });
    }

    private start() {
        this.connection.start().then(() => {
            Logger.debug([ 'portal-notification', 'signalR' ], 'Connection established');
            this.isConnected = true;
        })
            .catch(err => {
                const errorMessage = `Failed to establish the connection due to error "${JSON.stringify(err, getCircularReplacer)}}". Try refreshing this page to restart the connection.`;
                Logger.critical([ 'portal-notification', 'signalR' ], errorMessage, { state: store.state });
            });
    }
}

export type SignalRService = SignalRHelper;

export const getSignalRServiceInstance = (): SignalRService => {
    return new SignalRHelper();
};
